import { isPlainMember } from '../lib/userKind'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { makeStyles, tokens } from '@fluentui/react-components'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { Sidebar } from './Sidebar'
import { ImpersonationBanner } from '../components/ImpersonationBanner'
import { AnnouncementBanner } from '../components/AnnouncementBanner'
import { useToast } from '../components/ToastProvider'
import { ImpersonationPicker } from '../components/ImpersonationPicker'
import { NotificationsBell } from '../components/NotificationsBell'
import { TestDataToggle } from '../components/TestDataToggle'
import { ResetSessionButton } from '../components/ResetSessionButton'
import { TeamsChatFab } from '../components/TeamsChatFab'
import { LookAndFeelDrawer } from '../components/LookAndFeelDrawer'
import { WhatsNewDialog, readSeen } from '../components/WhatsNewDialog'
import { WelcomeDialog } from '../components/WelcomeDialog'
import { useImpersonation } from '../context/ImpersonationContext'
import { onboardingKind } from '../lib/onboarding'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f5f6fa',
  },
  column: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    background: '#FFFFFF',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: '#E0E0E0',
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: tokens.spacingHorizontalL,
    // clip, not hidden: hidden makes this a scroll container, and a sticky
    // descendant (the planning agenda) would stick to it instead of the page.
    overflowX: 'clip',
  },
})

export function AppLayout() {
  const styles = useStyles()
  const user = useEffectiveUser()
  const location = useLocation()
  const navigate = useNavigate()
  const { showToast } = useToast()

  /**
   * Say why a route bounced you.
   *
   * `RoleGuard` has always redirected with `state={{ accessDenied, from }}`, and
   * its own docblock said "AppLayout can display a feedback message" — but
   * nothing ever read it (`grep accessDenied` returned exactly one hit, the line
   * that set it). So a shared link to a page you cannot open silently became
   * your own home page, and the honest conclusion was that the link was broken.
   *
   * The state is cleared afterwards so a refresh, or a Back into this entry,
   * does not re-announce a redirect that already happened.
   */
  useEffect(() => {
    const state = location.state as { accessDenied?: boolean; from?: string } | null
    if (!state?.accessDenied) return
    showToast({
      title: 'You do not have access to that page',
      body: state.from ? `${state.from} is not available for your role.` : undefined,
      intent: 'warning',
    })
    navigate(location.pathname + location.search, { replace: true, state: null })
  }, [location, navigate, showToast])
  // A brand-new user (council with no FA, talent with no preferred FAs) is
  // shown the mandatory FADeclarationDialog on the home route. Suppress the
  // What's New popover until they're past that so the two modals don't stack;
  // they'll get the nudge on a later render once onboarding is done.
  const needsFaOnboarding =
    user?.role === 'council'
      ? !user.focus_area_code && !user.views_all_demands && !user.onboarding_completed_at
      : // A member with no talent profile is never asked to declare focus
        // areas (#1374) — counting them here would hold the What's New popover
        // back forever, waiting for an onboarding that is not coming.
        (user?.role === 'talent' || user?.role === 'member') && !isPlainMember(user)
        ? !user.preferred_focus_areas?.length
        : false

  // First run (nothing acknowledged in this browser yet) → the welcome stands
  // in for What's New. Read once per mount, so finishing the FA declaration
  // above leads straight into it rather than skipping it. Never during View-as:
  // the admin is not the new user.
  const { target } = useImpersonation()
  const [firstRun, setFirstRun] = useState(() => readSeen() === null)
  // Closing the welcome ends dialogs for this visit. If storage could not keep
  // that, What's New would read the missing marker and open straight after.
  const [welcomeDismissed, setWelcomeDismissed] = useState(false)
  const welcomeKind = firstRun && !target ? onboardingKind(user) : null

  return (
    <div className={styles.root}>
      <Sidebar />
      <div className={styles.column}>
        <ImpersonationBanner />
        <AnnouncementBanner />
        <header className={styles.header}>
          <TestDataToggle />
          <NotificationsBell />
          <ImpersonationPicker />
          <ResetSessionButton />
        </header>
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
      <TeamsChatFab canFileTicket />
      <LookAndFeelDrawer />
      {!needsFaOnboarding &&
        !welcomeDismissed &&
        (welcomeKind ? (
          <WelcomeDialog
            kind={welcomeKind}
            onClose={() => {
              setFirstRun(false)
              setWelcomeDismissed(true)
            }}
          />
        ) : (
          <WhatsNewDialog />
        ))}
    </div>
  )
}
